import { ApiError, type Actor, type Versioned } from "@baton/api";
import type { QueryClient, QueryKey } from "@tanstack/query-core";
import { deepEqual } from "./optimistic.ts";
import type { EntityQueue } from "./serialise.ts";

/**
 * FR-015-044, `contracts/client-api.md` § The sequence, step 5: on a 412 never roll back.
 * Refetch the entity and ask whose write won. When this tab's own writes produced the version
 * that won (the entity queue's record: a response this tab received, or the echo of a derived
 * write its request made), the 412 is not a conflict with anybody: the pending change is resent
 * against that version and no notice is raised (2026-10-02, the owner's report of a notice that
 * named the reader after the reader's own change). Otherwise raise `ConflictNotice` in the affected panel with the
 * actor from `updated_by`, the fields the winning write changed and the pending change
 * re-presented, and offer Apply mine (resend with the new version) and Discard, so there is
 * neither a silent overwrite nor a silent discard. Every resend runs in the entity's queue, so a
 * further edit made meanwhile waits for it and uses the version it returned.
 */
export interface Conflict<E extends Versioned> {
  readonly id: string;
  readonly entityKey: QueryKey;
  /** The actor whose write won, from the refetched entity. */
  readonly actor: Actor | null;
  readonly fresh: E;
  readonly pending: Readonly<Partial<E>>;
  /** The compared fields the winning write changed, in the order the host listed them. */
  readonly changed: readonly string[];
  readonly apply: () => Promise<E>;
  readonly discard: () => void;
}

/** A 412 resent because this tab's own write had won, or a conflict with another writer. */
export type Reconciled<E extends Versioned> = { readonly kind: "resent"; readonly entity: E } | { readonly kind: "conflict"; readonly conflict: Conflict<E> };

export interface ConflictStore {
  readonly conflicts: readonly Conflict<Versioned>[];
  /** The conflicts raised against one entity, for the panel that shows it. */
  forEntity(entityKey: QueryKey): readonly Conflict<Versioned>[];
  subscribe(listener: () => void): () => void;
  /** Drop every conflict, on sign-out and on a restore discard. */
  clear(): void;
}

export interface ReconcileOptions<E extends Versioned & { updated_by?: Actor }> {
  readonly queryClient: QueryClient;
  readonly entityKey: QueryKey;
  /** The entity's id, its key in the entity queue. */
  readonly entityId: string;
  /** The entity the write was built on: what the reader saw before the pending change. */
  readonly base: E;
  /** The fields a reader recognises, compared between what was shown and the fresh copy to say what the winning write changed. */
  readonly compare: readonly (keyof E & string)[];
  readonly pending: Readonly<Partial<E>>;
  /** `GET` the entity again; the fresh copy carries the version that won. */
  readonly refetch: () => Promise<E>;
  /** Resend the pending change against the fresh version. */
  readonly resend: (version: number) => Promise<E>;
}

export interface Reconciler extends ConflictStore {
  reconcile<E extends Versioned & { updated_by?: Actor }>(options: ReconcileOptions<E>): Promise<Reconciled<E>>;
}

let sequence = 0;

export function createReconciler(queue: EntityQueue): Reconciler {
  let conflicts: Conflict<Versioned>[] = [];
  const listeners = new Set<() => void>();
  const notify = () => {
    for (const listener of listeners) listener();
  };
  const remove = (id: string) => {
    conflicts = conflicts.filter((c) => c.id !== id);
    notify();
  };
  async function reconcile<E extends Versioned & { updated_by?: Actor }>(options: ReconcileOptions<E>): Promise<Reconciled<E>> {
    const { queryClient, entityKey, entityId } = options;
    // What the screen showed when the write was answered 412: the base with this tab's own earlier responses and the pending change on it.
    const shown = queryClient.getQueryData<E>(entityKey) ?? options.base;
    const fresh = await options.refetch();
    queryClient.setQueryData(entityKey, fresh);
    const resend = () => queue.run(entityId, fresh.version, options.resend);
    if (queue.produced(entityId, fresh.version)) {
      try {
        const entity = await resend();
        queryClient.setQueryData(entityKey, entity);
        return { kind: "resent", entity };
      } catch (error) {
        // Another writer landed between the refetch and the resend: that one is a conflict to present.
        if (error instanceof ApiError && error.status === 412) return reconcile(options);
        throw error;
      }
    }
    // A pending field is compared with what the reader saw before changing it; any other with what the screen showed.
    const changed = options.compare.filter((field) => !deepEqual((field in options.pending ? options.base : shown)[field], fresh[field]));
    sequence += 1;
    const id = `conflict-${sequence}`;
    const conflict: Conflict<E> = {
      id,
      entityKey,
      actor: fresh.updated_by ?? null,
      fresh,
      pending: options.pending,
      changed,
      apply: async () => {
        const result = await resend();
        queryClient.setQueryData(entityKey, result);
        remove(id);
        return result;
      },
      discard: () => {
        remove(id);
      },
    };
    conflicts = [...conflicts, conflict];
    notify();
    return { kind: "conflict", conflict };
  }
  return {
    get conflicts() {
      return conflicts;
    },
    forEntity: (entityKey) => conflicts.filter((c) => JSON.stringify(c.entityKey) === JSON.stringify(entityKey)),
    subscribe(listener) {
      listeners.add(listener);
      return () => void listeners.delete(listener);
    },
    clear() {
      conflicts = [];
      notify();
    },
    reconcile,
  };
}
